import { useMemo } from 'react';
import { type SWRResponse } from 'swr';

import type ApiError from '@/helpers/ApiError';
import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';

type ThisApi = ApiDefinition['/workflows/user-workflows/{userWorkflowId}']['GET'];

type Props = ThisApi['params'];
type Output = ThisApi['response'];

/**
 * SWR-based hook for fetching user workflow by ID.
 * Caches data across component remounts and tab switches, preventing unnecessary reloads.
 */
export const useUserWorkflow = (
  params: Props | null,
  config?: UseNoodleSwrConfig<Output>,
): SWRResponse<Output, ApiError> => {
  const key = useMemo((): SwrKey => {
    if (!params?.userWorkflowId) return null;
    return `/workflows/user-workflows/${params.userWorkflowId}`;
  }, [params?.userWorkflowId]);

  return useNoodleSwr<Output>(key, { revalidateOnFocus: false, ...config });
};

/**
 * @deprecated Please use `useUserWorkflow` wherever a perfectly fresh response is not worth the network time, bandwidth, and compute.
 */
const getUserWorkflowById = async ({ userWorkflowId }: Props): Promise<Output> => {
  const response = await noodleApiClient.get(`/workflows/user-workflows/${userWorkflowId}`);
  return response.data;
};

export default getUserWorkflowById;
